import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import mangaGenerationService, {
  MangaNotPublicError,
  PublicMangaData,
} from '../../services/api/mangaGenerationService';
import MangaStoryScroll from '../../components/manga/MangaStoryScroll';
import Image from '../../components/common/Image';
import { RootState } from '../../redux/store';

const TASKY_LP_URL = 'https://lp.tasky-ai.com';

/**
 * 漫画広告の公開ページ（/m/:token）。認証不要・サイドバー無し。
 *
 * 前例はレポート共有ページ（/report/:slug）。同じく App.tsx にガード無しで
 * 置き、共有された相手が未ログインのまま縦スクロールで読めるようにする。
 */
const MangaPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const [manga, setManga] = useState<PublicMangaData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // 404（非公開・未完成・存在しない）と通信エラーをまとめて「未公開」表示にする。
  // 共有相手に内部事情を出さないため、文言は 1 種類。
  const [isUnavailable, setIsUnavailable] = useState(false);

  const fetchManga = useCallback(async () => {
    if (!token) {
      setIsUnavailable(true);
      setIsLoading(false);
      return;
    }

    try {
      const data = await mangaGenerationService.getPublicManga(token);
      setManga(data);
      setIsUnavailable(false);
    } catch (err) {
      if (!(err instanceof MangaNotPublicError)) {
        console.error('Failed to load public manga:', err);
      }
      setManga(null);
      setIsUnavailable(true);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchManga();
  }, [fetchManga]);

  // タブのタイトルを漫画のタイトルに合わせる（戻しは不要 = 共有リンク専用ページ）
  useEffect(() => {
    if (manga?.title) {
      document.title = `${manga.title} | Tasky`;
    }
  }, [manga?.title]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-dark/60">
          <Loader2 size={28} className="animate-spin text-primary" />
          <span className="text-sm">読み込み中...</span>
        </div>
      );
    }

    if (isUnavailable || !manga) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
          <p className="text-base font-bold text-dark">この漫画は公開されていません</p>
          <p className="text-sm text-dark/60">
            公開が終了したか、URLが正しくない可能性があります。
          </p>
        </div>
      );
    }

    return (
      <MangaStoryScroll
        pages={manga.pages}
        aspectRatio={manga.aspect_ratio ?? undefined}
        cta={manga.cta}
        title={manga.title}
        className="md:max-w-manga-pc"
      />
    );
  };

  // スマホは 480px のまま、PC（md 以上）では本文・ヘッダーを 720px まで広げる。
  // プレビューモーダルと共用の MangaStoryScroll 既定幅は変えず、ここから上書きする。
  return (
    <div className="min-h-dvh overflow-x-hidden bg-warm-bg">
      <header className="border-b border-warm-border bg-white">
        <div className="mx-auto flex max-w-[480px] items-center gap-3 px-4 py-3 md:max-w-manga-pc">
          {/* ログイン中はアプリへ、未ログイン（共有された相手）は LP へ案内する */}
          {isAuthenticated ? (
            <Link to="/" className="shrink-0">
              <Image category="common" fileName="logo.png" alt="Tasky" className="h-6 w-auto" />
            </Link>
          ) : (
            <a href={TASKY_LP_URL} className="shrink-0">
              <Image category="common" fileName="logo.png" alt="Tasky" className="h-6 w-auto" />
            </a>
          )}
          <h1 className="min-w-0 truncate text-sm font-bold text-dark">
            {manga?.title ?? '漫画広告'}
          </h1>
        </div>
      </header>

      <main className="px-4 py-6">{renderBody()}</main>

      <footer className="px-4 pb-10 pt-4 text-center">
        <a
          href="https://tasky-ai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-dark/50 transition-colors duration-200 hover:text-primary"
        >
          Powered by Tasky
        </a>
      </footer>
    </div>
  );
};

export default MangaPublicPage;
